import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController, TextArea } from '@components-clean/core/text-area';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import {
    ExtendedRiskActionFields,
    ExtendedRiskObservationFields,
} from '@pages-clean/risk-assessment/risk-log/constants';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { NamedItemType, RiskActionType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import { RiskActionFields_RiskAction_responsibleFunction_NamedItem } from '@queries/generated/fragments.generated';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface AddActionProps {
    studyRef: string;
    onClose: () => void;
    onSubmit: (data: RiskActionFormInputs) => void;
    observation: ExtendedRiskObservationFields;
    action?: ExtendedRiskActionFields;
}

interface AdditionalInfoProps {
    observation: ExtendedRiskObservationFields;
}

type ExtendedResponsibleFunction = RiskActionFields_RiskAction_responsibleFunction_NamedItem & {
    groupedBy: string;
    disabled: boolean;
};

export type RiskActionFormInputs = {
    type: { id: string; text: string } | null;
    owner: { fullName: string; id: string; groupedBy: string; active: boolean } | null;
    responsibleFunction: ExtendedResponsibleFunction | null;
    requestedDate: Date | null;
    description: string | null;
    result: string | null;
    comments: string | null;
    expectedCompletionDate: Date | null;
    completedDate: Date | null;
};

const COMPONENT_NAME = 'add-action';

const AdditionalInfo = ({ observation }: AdditionalInfoProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'actions.dialogs.addAction.observationPanel' });
    const { t: tCommon } = useTranslation('common');

    if (!observation) {
        return (
            <div className="k-h-full k-p-8">
                <InfoField type="Error" message={t('observationDetails.error')} />
            </div>
        );
    }

    return (
        <div className={cn(styles.addActionObservationDetails)}>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.id')}</div>
                <div className={cn(styles.content, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {observation.recordId}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.status')}</div>
                <div className={cn(styles.content, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {observation?.status}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.classification')}</div>
                <div className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {observation?.classification ? observation.classification?.name : 'N/A'}
                    {observation?.classification && !observation?.classification?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm k-ml-auto')}>
                            <InactiveIcon
                                toolTipText={tCommon('inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.owner')}</div>
                <div className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {observation?.owner ? `${observation.owner?.firstName} ${observation.owner?.lastName}` : 'N/A'}
                    {observation?.owner && !observation?.owner?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm k-ml-auto')}>
                            <InactiveIcon
                                toolTipText={tCommon('inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex">
                <div className={cn(styles.label)}>{t('fields.responsibleFunction')}</div>
                <div className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-align-items-center')}>
                    {observation?.responsibleFunction ? observation.responsibleFunction?.name : 'N/A'}
                    {observation?.responsibleFunction && !observation?.responsibleFunction?.active && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm k-ml-auto')}>
                            <InactiveIcon
                                toolTipText={tCommon('inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex k-flex-col k-h-full" style={{ minHeight: 0, gap: '0.5rem' }}>
                <div className="k-d-flex k-flex-col k-h-full" style={{ flex: 3, minHeight: 0 }}>
                    <div className={cn(styles.label)}>{t('fields.description')}</div>
                    <div className={cn(styles.content)} style={{ overflow: 'auto' }}>
                        <TextArea
                            data-testid="add-edit-action-observation-details-description"
                            readOnly
                            value={observation?.description ?? undefined}
                            className="k-h-full"
                        />
                    </div>
                </div>
                <div
                    className="k-d-flex k-flex-col k-h-full"
                    style={{ flex: observation.comments ? 1 : 0.5, minHeight: 0 }}
                >
                    <div className={cn(styles.label)}>{t('fields.comments')}</div>
                    <div className={cn(styles.content)} style={{ overflow: 'auto' }}>
                        <TextArea
                            data-testid="add-edit-action-observation-details-comments"
                            readOnly
                            value={observation?.comments ?? undefined}
                            className="k-h-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const AddActionDialog = ({ studyRef, onClose, onSubmit, observation, action }: AddActionProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'actions.dialogs.addAction' });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const types = Object.values(RiskActionType)
        .filter((type) => type !== RiskActionType.NONE)
        .map((type) => ({
            id: type,
            text: tRoot(`ram:riskLog.shared.${String(type).toLowerCase()}`),
        }));

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: studyRef,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });

    const [owners, setOwners] = useState<
        ({ fullName: string; id: string; groupedBy: string; active: boolean } | null)[] | undefined
    >([]);

    const [loadOwnerData, { data: ownerData, loading: loadingOwners }] = useLazyQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS);

    useEffect(() => {
        loadOwnerData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, []);

    useEffect(() => {
        if (ownerData?.studyMembers) {
            const studyMembers = ownerData.studyMembers
                .filter((user) => user)
                .sort((aOwner, bOwner) => {
                    if (aOwner?.active !== bOwner?.active) {
                        return aOwner?.active ? -1 : 1;
                    }
                    const aName = `${aOwner?.firstName?.toLowerCase() ?? ''} ${aOwner?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${bOwner?.firstName?.toLowerCase() ?? ''} ${bOwner?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((user) => ({
                    fullName: `${user?.firstName} ${user?.lastName}`,
                    id: user?.id as string,
                    groupedBy: user?.active ? 'Active' : 'Inactive',
                    disabled: action?.owner
                        ? action.owner.name?.toLowerCase() ===
                          `${user?.firstName?.toLowerCase()} ${user?.lastName?.toLowerCase()}`
                            ? false
                            : !user?.active
                        : !user?.active,
                    active: !!user?.active,
                }));
            setOwners(studyMembers);
        }
    }, [ownerData?.studyMembers]);

    const {
        control,
        reset,
        getValues,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting, dirtyFields },
    } = useForm<RiskActionFormInputs>({
        mode: 'all',
        defaultValues: {
            type: action ? (types.find((type) => type.text === action.type) ?? null) : null,
            owner: action ? (owners?.find((o) => o?.id === action?.ownerId) ?? null) : null,
            responsibleFunction: action?.responsibleFunction
                ? {
                      ...action.responsibleFunction,
                      groupedBy: action.responsibleFunction.active ? 'Active' : 'Inactive',
                      disabled: false,
                  }
                : null,
            requestedDate: action?.requestDate ? javascriptDateWithoutTimeFromUTC(action.requestDate) : null,
            description: action?.description ?? '',
            result: action?.result ?? '',
            comments: action?.comments ?? '',
            expectedCompletionDate: action?.expectedCompleteDate
                ? javascriptDateWithoutTimeFromUTC(action.expectedCompleteDate)
                : null,
            completedDate: action?.completedDate ? javascriptDateWithoutTimeFromUTC(action.completedDate) : null,
        },
    });

    const dialogTitle = action
        ? `${tRoot('common:edit')} ${t('title')}: ${action.recordId}`
        : `${tRoot('common:add')} ${t('title')}`;
    const primaryBtnLabel = action ? tRoot('common:update') : tRoot('common:add');

    const createdBy = `${stringDateWithoutTimeFromUtc(action?.created, 'DD-MMM-YYYY')} by ${action?.createdBy}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(action?.updated, 'DD-MMM-YYYY')} by ${action?.updatedBy}`;

    useEffect(() => {
        if (action && owners && owners.length > 0) {
            reset({
                ...getValues(),
                owner: owners.find((ownerObject) => ownerObject?.id === action.ownerId) ?? null,
            });
        }
    }, [owners]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges('add-risk-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="add-edit-action-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            initialHeight={action ? 752 : 677}
            initialWidth={580}
            createdUpdatedInfo={action ? <CreatedUpdatedInfo created={createdBy} updated={updatedBy} /> : undefined}
            createdUpdatedBackgroundColour="gray"
            additionalInfo={<AdditionalInfo observation={observation} />}
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observationPanel.title')}
            setHeight="fit-content"
            bodyPadding="none"
        >
            <div className={cn('k-w-full k-h-full k-d-flex-col k-pt-xs', styles.rightBorderStyle)}>
                {action && (
                    <div className={cn(styles.editActionMetaData, 'k-py-xs k-pl-md')}>
                        <div className="k-d-flex k-gap-3">
                            <div className="k-font-medium">{t('metaDataLabels.status')}</div>
                            <div className={cn(styles.editActionDialogTopInfoLabel)}>{action.status}</div>
                        </div>
                    </div>
                )}
                <form id="add-edit-risk" className="k-w-full k-h-full k-pl-md k-pr-md k-pb-md k-pt-sm">
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="type"
                                label={t('fields.type')}
                                data-testid="risk-action-field-type"
                                data={types ?? []}
                                dataItemKey="id"
                                textField="text"
                                filterable
                                allowClear
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="owner"
                                label={t('fields.owner')}
                                data-testid="risk-action-field-owner"
                                data={owners ?? []}
                                loading={loadingOwners}
                                dataItemKey="id"
                                textField="fullName"
                                groupField="groupedBy"
                                activeField="active"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="responsibleFunction"
                                label={t('fields.responsibleFunction')}
                                data-testid="risk-action-field-responsible-function"
                                data={
                                    responsibleFunctionsData?.namedItemsByTypeForStudy
                                        ? mapNamedItems(
                                              responsibleFunctionsData.namedItemsByTypeForStudy,
                                              action?.responsibleFunction?.name
                                                  ? [action?.responsibleFunction.name]
                                                  : undefined,
                                          )
                                        : []
                                }
                                disabled={loadingResponsibleFunctions}
                                loading={loadingResponsibleFunctions}
                                dataItemKey="id"
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                filterable
                                allowClear
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <DatePickerController
                                control={control}
                                name="requestedDate"
                                label={t('fields.requestedDate')}
                                data-testid="risk-action-field-requested-date"
                                fillMode="solid"
                                required
                            />
                        </div>
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="description"
                            data-testid="risk-action-field-description"
                            label={t('fields.description')}
                            required
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="result"
                            data-testid="risk-action-field-result"
                            label={t('fields.result')}
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="comments"
                            data-testid="risk-action-field-comments"
                            label={t('fields.comments')}
                        />
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <DatePickerController
                                control={control}
                                name="expectedCompletionDate"
                                label={t('fields.expectedCompletionDate')}
                                data-testid="risk-action-field-expected-completion-date"
                                fillMode="solid"
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', styles.minWidthFix, styles.dropdownFix)}>
                            <DatePickerController
                                control={control}
                                name="completedDate"
                                label={t('fields.completedDate')}
                                data-testid="risk-action-field-completed-date"
                                fillMode="solid"
                                readonly={observation.closedDate !== null && action?.completedDate !== null}
                            />
                        </div>
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default AddActionDialog;
